import { transform, cloneDeep, isNil, merge } from 'lodash-es'
import { appVersion } from 'src/consts.js'
import { useAppStore } from 'src/stores/appStore.js'
import { trans } from 'src/utils/i18n.js'
import { computed, ref, watch } from 'vue'

/**
 * @deprecated DONT NEED
 */
export const defaultPlanFilters = {
    plan_preset: null,
    only_active: true,
    only_favourites: false,
}

/**
 * Sets up useStorage using a merge strategy that ensures the only keys that are retained are the ones passed in.
 * This ensures that old filters / outdated keys are properly removed when changed by the developer
 */
export function usePersistedFilters(key, filters) {
    const filterDefaults = cloneDeep(filters)
    const appliedFilters = ref(filters)

    function saveFilters() {
        window.localStorage.setItem(key, JSON.stringify(appliedFilters.value))
    }

    function loadFilters() {
        let previousData = JSON.parse(window.localStorage.getItem(key))
        if (!previousData) return // No previous persisted data?

        for (let filterKey in filters) {
            // Do we even have old data persisted for this filter?
            if (isNil(previousData[filterKey])) continue

            let filter = filters[filterKey]
            if (filter instanceof AdvancedFilter) {
                filter.load(previousData[filterKey])
            } else {
                filters[filterKey] = previousData[filterKey]
            }
        }
    }

    /**
     * Reduce the applied filters down to a simple object of key value pairs to send to the server
     */
    function serialiseFilters(filters) {
        let serialised = transform(filters, (out, filter, filterKey) => {
            out[filterKey] = filter && Object.hasOwn(filter, 'value')
                ? filter.value
                : cloneDeep(filter)
            return out
        }, {})

        serialised.version = appVersion
        return serialised
    }

    function resetFilters(newData = null) {
        let copy = cloneDeep(filterDefaults)
        if (newData) copy = merge(copy, newData)
        appliedFilters.value = copy
    }

    loadFilters()
    const serialisedFilters = computed(() => ({ filters: serialiseFilters(appliedFilters.value) }))
    watch(serialisedFilters, saveFilters)

    return {
        saveFilters,
        filters: appliedFilters,
        serialisedFilters,
        resetFilters,
    }
}

export class AdvancedFilter {
    constructor(typeLabel, metadata = {}) {
        this.typeLabel = typeLabel // i18n translated label for this filter. e.g "Project Manager"
        this.displayValue = null // e.g user's name or the lookup's translated value
        this.value = null // e.g user's id or lookup id etc. Actual value that is sent to the server
        this.rawValue = null // original selected value (depending on filter component e.g user object, office object etc)
        this.metadata = metadata // any extra data to go with the filter
    }

    setSelection(displayValue, value, rawValue) {
        this.displayValue = trans(displayValue || '')
        this.value = value
        this.rawValue = rawValue
    }

    reset() {
        this.displayValue = null
        this.value = null
        this.rawValue = null
    }

    load(data) {
        this.displayValue = data.displayValue
        this.metadata = data.metadata
        this.value = data.value
        this.rawValue = data.rawValue
    }
}

/**
 * Create the correct number of business group advanced filters
 */
export function makeBusinessGroupAdvancedFilters() {
    const appStore = useAppStore()
    return appStore.businessGroupTypesWithoutCountry.reduce((out, type) => {
        out[type.value] = new AdvancedFilter(trans(type.value))
        return out
    }, {})
}

/**
 * Make a comment set of filters that are used on various pages that have plan advanced filtering
 */
export function makeCommonPlanFilters(extraFilters = {}) {
    return {
        project_manager_id: new AdvancedFilter(trans('project_manager')),
        project_director_id: new AdvancedFilter(trans('project_director')),
        project_finance_specialist_id: new AdvancedFilter(trans('project_finance_specialist')),
        commercial_leader_id: new AdvancedFilter(trans('commercial_leader')),
        business_leader_id: new AdvancedFilter(trans('business_leader')),
        is_accredited_person_required: new AdvancedFilter(trans('is_registered_or_accredited_person_to_oversee_work')),
        pm_level_exception: new AdvancedFilter(trans('pm_level_exception')),
        client: new AdvancedFilter(trans('client')),
        end_customer: new AdvancedFilter(trans('end_customer')),
        market_sector_id: new AdvancedFilter(trans('market_sector')),
        service_type_id: new AdvancedFilter(trans('service_type')),
        services_provided: new AdvancedFilter(trans('services_provided')),
        contract_type: new AdvancedFilter(trans('contract_type')),
        parent_customer: new AdvancedFilter(trans('parent_customer')),
        region: new AdvancedFilter(trans('region')),
        project_risk_indicator: new AdvancedFilter(trans('project_risk_indicator')),
        client_feedback_status_id: new AdvancedFilter(trans('is_client_feedback_received')),
        client_executed_contract: new AdvancedFilter(trans('client_executed_contract')),
        is_client_agreement_documented: new AdvancedFilter(trans('is_client_agreement_documented')),
        project_sheet: new AdvancedFilter(trans('has_project_sheet')),
        is_design_work_required: new AdvancedFilter(trans('is_design_work_required')),
        only_essential_tasks: new AdvancedFilter(trans('exclude_from_my_actions')),
        is_field_work_required: new AdvancedFilter(trans('is_field_work_required')),
        lesson_learned_source: new AdvancedFilter(trans('lesson_learned_source')),
        has_rnd_assessment: new AdvancedFilter(trans('has_rnd_assessment')),
        plan_status: new AdvancedFilter(trans('plan_status')),
        mpr_status: new AdvancedFilter(trans('mpr_status')),
        project_specific_insurance_type: new AdvancedFilter(trans('project_specific_insurance')),
        relevant_programmes: new AdvancedFilter(trans('relevant_programmes')),
        min_revenue_budget: new AdvancedFilter(trans('min_revenue_budget')),
        max_revenue_budget: new AdvancedFilter(trans('max_revenue_budget')),
        delivery_type: new AdvancedFilter(trans('delivery_type')),
        ...makeBusinessGroupAdvancedFilters(),
        ...cloneDeep(defaultPlanFilters),
        ...extraFilters,
    }
}
